import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Clock, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { tenantScopedStorageKey } from '@/utils/tenantScopedKeys'
import { useSetupStatus, type SetupTask } from '@/hooks/useSetupStatus'

export type { SetupTask } from '@/hooks/useSetupStatus'

export interface SetupChecklistProps {
  /** Conteo que ya entrega `/dashboard/stats`; evita descargar el catálogo completo. */
  activeProducts?: number
  initialTasks?: SetupTask[]
  initialLoading?: boolean
}

function readDismissed(tenantId: string | null): boolean {
  try {
    return localStorage.getItem(tenantScopedStorageKey('setup-checklist-dismissed', tenantId)) === 'true'
  } catch {
    return false
  }
}

export default function SetupChecklist({
  activeProducts,
  initialTasks,
  initialLoading = false,
}: SetupChecklistProps = {}) {
  const tenantId = useAuthStore((state) => state.tenantId)
  const navigate = useNavigate()
  const { tasks: loadedTasks, isLoading: statusLoading } = useSetupStatus({ activeProducts })
  const [dismissed, setDismissed] = useState(() => readDismissed(tenantId))
  const [expanded, setExpanded] = useState(true)

  const tasks = initialTasks ?? loadedTasks
  const loading = initialLoading || (initialTasks ? false : statusLoading)

  const handleDismiss = () => {
    try {
      localStorage.setItem(tenantScopedStorageKey('setup-checklist-dismissed', tenantId), 'true')
    } catch {
      /* almacenamiento no disponible */
    }
    setDismissed(true)
  }

  const handleTaskClick = (task: SetupTask) => {
    navigate(task.link)
  }

  const completedCount = tasks.filter((t) => t.completed).length
  const totalCount = tasks.length
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
  const allCompleted = completedCount === totalCount

  if (loading || dismissed || allCompleted) {
    return null
  }

  const highPriorityTasks = tasks.filter((t) => !t.completed && t.priority === 'high')
  const hasHighPriority = highPriorityTasks.length > 0

  return (
    <div className={cn(
      "rounded-2xl border shadow-sm overflow-hidden transition-all",
      hasHighPriority
        ? "border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white"
        : "border-sky-200 bg-gradient-to-br from-sky-50/60 to-white"
    )}>
      {/* Header */}
      <div className={cn(
        "px-4 sm:px-6 py-4 border-b",
        hasHighPriority ? "bg-emerald-50/80 border-emerald-100" : "bg-sky-50/80 border-sky-100"
      )}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className={cn(
              "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm",
              hasHighPriority ? "bg-brand" : "bg-sky-600"
            )}>
              <CheckCircle2 className="w-5 h-5 text-white" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={cn(
                "text-base sm:text-lg font-bold mb-0.5",
                hasHighPriority ? "text-gray-900" : "text-sky-950"
              )}>
                {hasHighPriority ? 'Pasos esenciales para activar tus ventas por WhatsApp' : 'Personaliza y optimiza tu Asistente'}
              </h3>
              <p className={cn(
                "text-xs sm:text-sm",
                hasHighPriority ? "text-emerald-900 font-medium" : "text-sky-800"
              )}>
                {allCompleted
                  ? '¡Excelente! Has completado todas las configuraciones recomendadas.'
                  : `${completedCount} de ${totalCount} completadas (${completionPercentage}%) — Sigue estos pasos para dejar tu tienda lista.`
                }
              </p>
              {/* Progress Bar */}
              <div className="mt-2 w-full bg-white/50 rounded-full h-2 overflow-hidden">
                <div
                  className={cn(
                    "h-full transition-all duration-500 rounded-full",
                    hasHighPriority ? "bg-amber-500" : "bg-blue-500"
                  )}
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              aria-label={expanded ? 'Contraer lista de configuración' : 'Expandir lista de configuración'}
              className={cn(
                "p-1.5 rounded-lg transition-colors",
                hasHighPriority
                  ? "hover:bg-amber-200 text-amber-700"
                  : "hover:bg-blue-200 text-blue-700"
              )}
              title={expanded ? "Contraer" : "Expandir"}
            >
              <ChevronRight className={cn(
                "w-5 h-5 transition-transform",
                expanded && "rotate-90"
              )} />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Descartar lista de configuración"
              className={cn(
                "p-1.5 rounded-lg transition-colors",
                hasHighPriority
                  ? "hover:bg-amber-200 text-amber-700"
                  : "hover:bg-blue-200 text-blue-700"
              )}
              title="Descartar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      {expanded && (
        <div className="p-4 sm:p-6 space-y-3">
          {tasks.map((task) => {
            const Icon = task.icon
            const priorityColors = {
              high: 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50/80',
              medium: 'border-slate-200 bg-white hover:bg-slate-50',
              low: 'border-slate-100 bg-white hover:bg-slate-50',
            }
            const priorityBadges = {
              high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
              medium: 'bg-amber-50 text-amber-700 border-amber-200',
              low: 'bg-slate-50 text-slate-600 border-slate-200',
            }
            const priorityLabels = {
              high: 'Esencial',
              medium: 'Recomendado',
              low: 'Opcional',
            }

            return (
              <button
                key={task.id}
                type="button"
                onClick={() => !task.completed && handleTaskClick(task)}
                disabled={task.completed}
                className={cn(
                  "w-full flex items-start gap-4 p-4 rounded-xl border transition-all text-left group",
                  task.completed
                    ? "border-emerald-200 bg-emerald-50/50 opacity-80 cursor-default"
                    : priorityColors[task.priority] + " cursor-pointer shadow-sm hover:shadow"
                )}
              >
                {/* Icon/Status */}
                <div className="flex-shrink-0">
                  {task.completed ? (
                    <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center shadow-sm">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                  ) : (
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105 shadow-sm",
                      task.priority === 'high' ? "bg-brand" :
                      task.priority === 'medium' ? "bg-amber-600" :
                      "bg-slate-400"
                    )}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={cn(
                      "font-semibold text-sm sm:text-base",
                      task.completed ? "text-emerald-950" : "text-gray-900"
                    )}>
                      {task.title}
                    </h4>
                    {!task.completed && (
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full border font-medium",
                        priorityBadges[task.priority]
                      )}>
                        {priorityLabels[task.priority]}
                      </span>
                    )}
                  </div>
                  <p className={cn(
                    "text-xs sm:text-sm mb-2",
                    task.completed ? "text-emerald-700" : "text-gray-600"
                  )}>
                    {task.description}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {task.estimatedTime}
                    </span>
                    {task.completed && (
                      <span className="text-emerald-600 font-semibold">✓ Completado</span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                {!task.completed && (
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                )}
              </button>
            )
          })}
        </div>
      )}

      {/* Footer */}
      {expanded && !allCompleted && (
        <div className={cn(
          "px-4 sm:px-6 py-3 border-t text-center",
          hasHighPriority ? "bg-emerald-50/40 border-emerald-100" : "bg-sky-50/40 border-sky-100"
        )}>
          <p className="text-xs text-gray-600">
            Completar estas configuraciones activará tu asistente de ventas para atender y cerrar pedidos por WhatsApp
          </p>
        </div>
      )}
    </div>
  )
}
